import { computed, readonly, ref } from 'vue'

// Module-level refs — singleton state shared across all callers
const isCollapsed = ref(localStorage.getItem('sidebarCollapsed') === 'true')
const isForcedCollapse = ref(false)
const isForcedCollapseExpanded = ref(false)

/**
 * True when the sidebar should display labels (i.e. is effectively expanded).
 *
 * - Forced-collapse mode: only expanded when the user has temporarily expanded it.
 * - Normal mode: expanded when not collapsed.
 */
const isEffectivelyExpanded = computed(() => {
    if (isForcedCollapse.value) {
        return isForcedCollapseExpanded.value
    }
    return !isCollapsed.value
})

/**
 * True when the sidebar is rendering as an overlay on top of page content
 * (i.e. the page forces it collapsed but the user expanded it anyway).
 */
const isOverlay = computed(() => {
    return isForcedCollapse.value && isForcedCollapseExpanded.value
})

function toggleCollapse() {
    // Pages that force the sidebar collapsed get a temporary, non-persisted expansion
    if (isForcedCollapse.value) {
        isForcedCollapseExpanded.value = !isForcedCollapseExpanded.value
        return
    }

    isCollapsed.value = !isCollapsed.value
    localStorage.setItem('sidebarCollapsed', String(isCollapsed.value))
}

function setForcedCollapse(value) {
    isForcedCollapse.value = Boolean(value)
    isForcedCollapseExpanded.value = false
}

export function useSidebarState() {
    return {
        isCollapsed: readonly(isCollapsed),
        isForcedCollapse: readonly(isForcedCollapse),
        isEffectivelyExpanded,
        isOverlay,
        toggleCollapse,
        setForcedCollapse,
    }
}
